import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Copy, Check, ExternalLink, Terminal, Key, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import EmbedSdkDemo from '@/components/developers/EmbedSdkDemo';
import EmbedSnippetGenerator from '@/components/developers/EmbedSnippetGenerator';
import RateLimitInsights from '@/components/developers/RateLimitInsights';
import EmbedOriginsManager from '@/components/developers/EmbedOriginsManager';
import EmbedAlertsManager from '@/components/developers/EmbedAlertsManager';
import AuditTrailViewer from '@/components/developers/AuditTrailViewer';
import WebhookDeliveriesPanel from '@/components/developers/WebhookDeliveriesPanel';
import { useBrandName } from '@/lib/branding';

/**
 * Developer Portal — INZARA Management API v1
 *
 * Reads the filtered OpenAPI spec from /api/mgmt/v1/openapi.json and
 * renders a lightweight browsable reference: grouped endpoint cards,
 * copy-paste curl snippets, method badges, expandable details.
 *
 * Auth strip at the top lets the developer paste a Bearer key so the
 * curl snippets they copy already include it — cuts onboarding from
 * "read docs, paste key manually" to "paste once, go".
 */

const METHOD_COLORS = {
  GET: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  POST: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  PATCH: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  PUT: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  DELETE: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
};

// Group headings — order matters (first match wins; also drives sidebar order)
export const GROUPS = [
  { key: 'api-keys', label: 'API Keys', match: (p) => p.includes('/api-keys') || p.endsWith('/scopes') },
  { key: 'organizations', label: 'Organizations', match: (p) => p.includes('/organizations') },
  { key: 'webhooks', label: 'Webhooks', match: (p) => p.includes('/webhooks') },
  { key: 'identity', label: 'Identity & SCIM', match: (p) => p.includes('/admin/identity') },
  { key: 'policies', label: 'Policies (RLS & Masking)', match: (p) => p.includes('/admin/policies') },
  { key: 'roles', label: 'Roles & Permissions', match: (p) => p.includes('/admin/roles') || p.includes('/admin/permissions') },
  { key: 'admin-users', label: 'Tenant Users', match: (p) => p.includes('/admin/users') },
  { key: 'feature-flags', label: 'Feature Flags', match: (p) => p.includes('/feature-flags') },
  { key: 'cost-alerts', label: 'Cost Alerts', match: (p) => p.includes('/cost-alerts') },
  { key: 'governance', label: 'Governance Cleanup', match: (p) => p.includes('/admin/governance') },
  { key: 'sessions', label: 'Admin Sessions', match: (p) => p.includes('/admin/sessions') },
  { key: 'audit', label: 'Audit & Certifications', match: (p) => p.includes('/admin/audit') || p.includes('/certifications') },
  { key: 'usage', label: 'Usage & Rate Limits', match: (p) => p.includes('/usage') || p.includes('/rate-limit') },
  { key: 'budget', label: 'Budgets', match: (p) => p.includes('/budget') },
  { key: 'embed', label: 'Embed Sessions', match: (p) => p.includes('/embed/') },
  { key: 'users', label: 'Workspace Users', match: (p) => p.includes('/workspaces') && p.includes('/users') },
  { key: 'workspaces', label: 'Workspaces', match: (p) => p.includes('/workspaces') },
  { key: 'platform', label: 'Platform', match: (p) => p.includes('/platform/') },
  { key: 'system', label: 'System', match: (p) => p.endsWith('/health') || p.endsWith('/status') || p.endsWith('/openapi.json') },
];

export function classify(path) {
  for (const g of GROUPS) if (g.match(path)) return g.key;
  return 'other';
}

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        } catch (_) { /* clipboard blocked — no-op */ }
      }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border border-border/40 bg-background/60 hover:bg-muted/40 transition-colors"
      data-testid={testid}
    >
      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
    </button>
  );
}

function EndpointCard({ path, method, op, apiBase, bearerKey, workspaces = [], workspacesLoading = false }) {
  const [open, setOpen] = useState(false);
  const bodySample = useMemo(() => sampleBody(op), [op]);
  const curl = useMemo(
    () => buildCurl(apiBase, method, path, bearerKey, bodySample),
    [apiBase, method, path, bearerKey, bodySample],
  );

  const pathParams = useMemo(() => (path.match(/\{([^}]+)\}/g) || []).map(s => s.slice(1, -1)), [path]);
  const [paramVals, setParamVals] = useState({});
  const [bodyText, setBodyText] = useState(() => (bodySample ? JSON.stringify(bodySample, null, 2) : ''));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const destructive = method === 'DELETE';
  const hasBody = ['POST', 'PATCH', 'PUT'].includes(method);
  const canRun = !!bearerKey?.trim() && pathParams.every(p => (paramVals[p] || '').trim());
  const cardId = path.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');

  const runRequest = async () => {
    if (destructive && !window.confirm(`Run ${method} ${path}? This can modify or delete data.`)) return;
    let url = `${apiBase}${path}`;
    for (const p of pathParams) url = url.replace(`{${p}}`, encodeURIComponent(paramVals[p]));
    const opts = { method, headers: { Authorization: `Bearer ${bearerKey.trim()}` } };
    if (hasBody && bodyText.trim()) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = bodyText;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await fetch(url, opts);
      const text = await res.text();
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
      setResult({ status: res.status, ok: res.ok, body: pretty || '(empty response)' });
    } catch (e) {
      setResult({ status: 0, ok: false, body: `Request failed: ${e}` });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-border/40 bg-background/40 overflow-hidden"
      data-testid={`endpoint-card-${method}-${path.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}
    >
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 text-left"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        <span className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${METHOD_COLORS[method] || 'bg-muted text-foreground border-border'}`}>
          {method}
        </span>
        <code className="text-xs font-mono flex-1 truncate">{path}</code>
        {op.summary && (
          <span className="text-[11px] text-muted-foreground hidden md:block truncate max-w-md">
            {op.summary}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 py-3 border-t border-border/25 space-y-3 text-xs">
          {op.description && (
            <p className="text-muted-foreground leading-relaxed">{op.description}</p>
          )}
          {op.parameters && op.parameters.length > 0 && (
            <div>
              <div className="font-medium mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Parameters</div>
              <ul className="space-y-1">
                {op.parameters.map((p, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <code className="font-mono text-[11px]">{p.name}</code>
                    <span className="text-[10px] text-muted-foreground">{p.in}</span>
                    {p.required && <span className="text-[10px] text-rose-300">required</span>}
                    {p.schema?.type && <span className="text-[10px] text-muted-foreground">· {p.schema.type}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">curl</div>
              <CopyButton
                text={curl}
                testid={`copy-curl-${method}-${path.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}
              />
            </div>
            <pre className="bg-black/40 border border-border/30 rounded p-2.5 text-[11px] leading-relaxed overflow-x-auto whitespace-pre">
{curl}
            </pre>
          </div>

          {/* Interactive Try-It — runs live against this host with your pasted key. */}
          <div data-testid={`tryit-${method}-${cardId}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Try it</div>
              {!bearerKey?.trim() && <span className="text-[10px] text-amber-300">paste an API key above to enable</span>}
              {destructive && <span className="text-[10px] text-rose-300">destructive — asks to confirm</span>}
            </div>
            {pathParams.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                {pathParams.map(p => (
                  (p === 'workspace_id' && workspaces.length > 0) ? (
                    <select
                      key={p}
                      value={paramVals[p] || ''}
                      onChange={e => setParamVals(v => ({ ...v, [p]: e.target.value }))}
                      className="font-mono text-[11px] px-2 py-1 rounded border border-border/40 bg-background/60"
                      data-testid={`tryit-param-${p}`}
                    >
                      <option value="">Select a workspace…</option>
                      {workspaces.map(w => (
                        <option key={w.workspace_id} value={w.workspace_id}>
                          {(w.name || w.workspace_id)} — {w.workspace_id}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      key={p}
                      value={paramVals[p] || ''}
                      onChange={e => setParamVals(v => ({ ...v, [p]: e.target.value }))}
                      placeholder={p === 'workspace_id' && workspacesLoading ? 'loading workspaces…' : p}
                      spellCheck={false}
                      className="font-mono text-[11px] px-2 py-1 rounded border border-border/40 bg-background/60"
                      data-testid={`tryit-param-${p}`}
                    />
                  )
                ))}
              </div>
            )}
            {hasBody && (
              <textarea
                value={bodyText}
                onChange={e => setBodyText(e.target.value)}
                spellCheck={false}
                rows={4}
                placeholder="{ }"
                className="w-full font-mono text-[11px] px-2 py-1.5 rounded border border-border/40 bg-background/60 mb-2"
                data-testid={`tryit-body-${cardId}`}
              />
            )}
            <button
              onClick={runRequest}
              disabled={!canRun || running}
              className={`text-[11px] px-3 py-1.5 rounded font-medium transition-colors ${
                !canRun || running
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : destructive
                    ? 'bg-rose-600 text-white hover:bg-rose-500'
                    : 'bg-primary text-primary-foreground hover:opacity-90'
              }`}
              data-testid={`tryit-run-${method}-${cardId}`}
            >
              {running ? 'Running…' : `Run ${method}`}
            </button>
            {result && (
              <div className="mt-2" data-testid={`tryit-response-${cardId}`}>
                <div className={`text-[11px] font-mono mb-1 ${result.ok ? 'text-emerald-400' : 'text-rose-400'}`}>
                  HTTP {result.status || '—'}
                </div>
                <pre className="bg-black/40 border border-border/30 rounded p-2.5 text-[11px] leading-relaxed overflow-auto max-h-72 whitespace-pre">{result.body}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Fabricate a realistic request body from the operation's JSON schema.
// We don't need perfection — just something copy-pasteable.
function sampleBody(op) {
  const schema = op?.requestBody?.content?.['application/json']?.schema;
  if (!schema) return null;
  const resolved = resolveSchema(schema);
  return exampleFromSchema(resolved);
}

function resolveSchema(schema) {
  // Pydantic surfaces $ref for named models — for the portal we just skip
  // resolution and render property placeholders instead.
  return schema;
}

function exampleFromSchema(schema) {
  if (!schema || typeof schema !== 'object') return {};
  if (schema.type === 'string') return schema.example || 'string';
  if (schema.type === 'integer') return schema.example ?? 0;
  if (schema.type === 'number') return schema.example ?? 0.0;
  if (schema.type === 'boolean') return schema.example ?? true;
  if (schema.type === 'array') return [exampleFromSchema(schema.items || {})];
  if (schema.type === 'object' && schema.properties) {
    const out = {};
    for (const [k, v] of Object.entries(schema.properties)) {
      out[k] = exampleFromSchema(v);
    }
    return out;
  }
  return {};
}

function buildCurl(apiBase, method, path, bearerKey, body) {
  const key = bearerKey?.trim() || 'sk_live_YOUR_KEY_HERE';
  const url = `${apiBase}${path}`;
  const parts = [`curl -X ${method} "${url}"`];
  parts.push(`  -H "Authorization: Bearer ${key}"`);
  if (body && ['POST', 'PATCH', 'PUT'].includes(method)) {
    parts.push(`  -H "Content-Type: application/json"`);
    parts.push(`  -d '${JSON.stringify(body, null, 2).replace(/\n/g, '\n     ')}'`);
  }
  return parts.join(' \\\n');
}

export default function DevelopersPage() {
  const APP = useBrandName();
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bearerKey, setBearerKey] = useState('');
  const [filter, setFilter] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');
  const [searchParams] = useSearchParams();

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  // Once a key is pasted, fetch the tenant's workspaces so the Try-It
  // console can offer a workspace picker instead of a blind ID field.
  useEffect(() => {
    const key = (bearerKey || '').trim();
    if (!key) { setWorkspaces([]); return undefined; }
    let cancelled = false;
    setWorkspacesLoading(true);
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/mgmt/v1/workspaces?limit=200`, {
          headers: { Authorization: `Bearer ${key}` },
        });
        if (!res.ok) { if (!cancelled) setWorkspaces([]); return; }
        const json = await res.json();
        if (!cancelled) setWorkspaces(Array.isArray(json?.data) ? json.data : []);
      } catch (e) {
        if (!cancelled) setWorkspaces([]);
      } finally {
        if (!cancelled) setWorkspacesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bearerKey, apiBase]);

  // Pre-fill the auth strip when arriving from the Platform Admin's
  // "Try in Portal" button (?key=sk_live_…). The key is never sent
  // anywhere from this page — it just lands in the input.
  useEffect(() => {
    const k = searchParams.get('key');
    if (k) setBearerKey(k);
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/mgmt/v1/openapi.json`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setSpec(data);
      } catch (e) {
        if (alive) setError(String(e?.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiBase]);

  const endpoints = useMemo(() => {
    if (!spec?.paths) return [];
    const rows = [];
    for (const [path, methods] of Object.entries(spec.paths)) {
      for (const [method, op] of Object.entries(methods)) {
        if (!['get', 'post', 'patch', 'put', 'delete'].includes(method)) continue;
        rows.push({ path, method: method.toUpperCase(), op, group: classify(path) });
      }
    }
    // Group order first, then method preference, then path
    const methodOrder = { GET: 0, POST: 1, PUT: 2, PATCH: 3, DELETE: 4 };
    const groupOrder = Object.fromEntries(GROUPS.map((g, i) => [g.key, i]));
    rows.sort((a, b) =>
      (groupOrder[a.group] ?? 99) - (groupOrder[b.group] ?? 99)
      || (methodOrder[a.method] ?? 99) - (methodOrder[b.method] ?? 99)
      || a.path.localeCompare(b.path),
    );
    return rows;
  }, [spec]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return endpoints.filter(e => {
      if (activeGroup !== 'all' && e.group !== activeGroup) return false;
      if (!q) return true;
      return (
        e.path.toLowerCase().includes(q)
        || e.method.toLowerCase().includes(q)
        || (e.op.summary || '').toLowerCase().includes(q)
      );
    });
  }, [endpoints, filter, activeGroup]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="developers-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Developer Portal</div>
          <h1 className="text-3xl font-semibold">Management API v1</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Host-facing REST plane for embedding {APP} analytics into your product.
            Authenticate with a <code className="text-xs px-1 rounded bg-muted/40">Bearer sk_live_…</code>
            or <code className="text-xs px-1 rounded bg-muted/40">sk_test_…</code> key. Never expose the key
            to the browser — mint sessions from your backend only.
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          <a
            href="/api/mgmt/v1/openapi.json"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="developers-openapi-link"
          >
            <ExternalLink className="w-3 h-3" /> openapi.json
          </a>
          <Link
            to="/help"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="developers-docs-link"
          >
            <BookOpen className="w-3 h-3" /> Product docs
          </Link>
        </div>
      </div>

      {/* Auth strip */}
      <div className="rounded-lg border border-border/40 bg-muted/10 px-4 py-3 mb-6">
        <div className="flex items-center gap-2 mb-1.5">
          <Key className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs font-medium">Paste your API key</span>
          <span className="text-[10px] text-muted-foreground">— injected into every curl below. Stored locally, never sent to {APP} from this page.</span>
        </div>
        <input
          value={bearerKey}
          onChange={(e) => setBearerKey(e.target.value)}
          placeholder="sk_live_… or sk_test_…"
          spellCheck={false}
          autoComplete="off"
          type="password"
          className="w-full font-mono text-xs px-3 py-1.5 rounded border border-border/40 bg-background/60 focus:outline-none focus:border-primary"
          data-testid="developers-bearer-input"
        />
      </div>

      {/* SDK demo — @inzara/embed-react */}
      <EmbedSdkDemo apiBase={apiBase} bearerKey={bearerKey} />

      {/* Embed snippet generator — iframe + SDK for any surface */}
      <EmbedSnippetGenerator apiBase={apiBase} bearerKey={bearerKey} />

      {/* Rate-limit insights — per-key usage vs limit */}
      <RateLimitInsights apiBase={apiBase} bearerKey={bearerKey} />

      {/* Allowed embed origins — per-workspace lock */}
      <EmbedOriginsManager apiBase={apiBase} bearerKey={bearerKey} />

      {/* Email alerts on origin changes */}
      <EmbedAlertsManager apiBase={apiBase} bearerKey={bearerKey} />

      {/* Audit trail — quick peek incl. embed origin changes */}
      <AuditTrailViewer apiBase={apiBase} bearerKey={bearerKey} />

      {/* Origin-change webhook deliveries */}
      <WebhookDeliveriesPanel apiBase={apiBase} bearerKey={bearerKey} />

      {/* Filter row */}
      <div className="flex items-center gap-3 mb-4">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by path, method, or summary…"
          className="flex-1 text-xs px-3 py-1.5 rounded border border-border/40 bg-background/60 focus:outline-none focus:border-primary"
          data-testid="developers-filter-input"
        />
        <div className="flex items-center gap-1 flex-wrap">
          <FilterChip
            label="All"
            active={activeGroup === 'all'}
            onClick={() => setActiveGroup('all')}
            testid="developers-group-all"
          />
          {GROUPS.map(g => (
            <FilterChip
              key={g.key}
              label={g.label}
              active={activeGroup === g.key}
              onClick={() => setActiveGroup(g.key)}
              testid={`developers-group-${g.key}`}
            />
          ))}
        </div>
      </div>

      {loading && <div className="text-xs text-muted-foreground">Loading OpenAPI spec…</div>}
      {error && (
        <div className="text-xs text-rose-300" data-testid="developers-error">
          Couldn&apos;t load spec: {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="text-[11px] text-muted-foreground mb-3">
            <Terminal className="w-3 h-3 inline mr-1" />
            Showing <span className="text-foreground">{filtered.length}</span> of {endpoints.length} endpoints
          </div>
          <div className="space-y-2" data-testid="developers-endpoint-list">
            {filtered.map((e, i) => (
              <EndpointCard
                key={`${e.method}-${e.path}-${i}`}
                path={e.path}
                method={e.method}
                op={e.op}
                apiBase={apiBase}
                bearerKey={bearerKey}
                workspaces={workspaces}
                workspacesLoading={workspacesLoading}
              />
            ))}
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground italic px-3 py-6 text-center">
                No endpoints match the current filter.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
        active
          ? 'border-primary/60 bg-primary/15 text-primary'
          : 'border-border/40 bg-background/40 text-muted-foreground hover:text-foreground'
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}
